import React, { useEffect } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { datadogRum } from '@datadog/browser-rum';

// Get configuration from server-injected state with fallback to environment variables
const getRumConfig = (serverConfig = {}) => {
  const config = {
    applicationId:
      serverConfig?.datadogRumApplicationId || process.env.REACT_APP_CBRE_BROWSER_DATADOG_RUM_APPLICATION_ID,
    clientToken: serverConfig?.datadogRumClientToken || process.env.REACT_APP_CBRE_BROWSER_DATADOG_RUM_CLIENT_TOKEN,
    site: serverConfig?.datadogRumSite || process.env.REACT_APP_CBRE_BROWSER_DATADOG_RUM_SITE || 'datadoghq.com',
    service:
      serverConfig?.datadogRumService ||
      process.env.REACT_APP_CBRE_BROWSER_DATADOG_RUM_SERVICE ||
      'pjm_technology_platform',
    env: serverConfig?.datadogRumEnv || process.env.REACT_APP_CBRE_BROWSER_DATADOG_RUM_ENV || 'rum-default',
    sessionSampleRate:
      parseInt(
        serverConfig?.datadogRumSessionSampleRate || process.env.REACT_APP_CBRE_BROWSER_DATADOG_RUM_SESSION_SAMPLE_RATE,
        10,
      ) || 100,
    sessionReplaySampleRate:
      parseInt(
        serverConfig?.datadogRumReplaySampleRate || process.env.REACT_APP_CBRE_BROWSER_DATADOG_RUM_REPLAY_SAMPLE_RATE,
        10,
      ) || 20,
    defaultPrivacyLevel:
      serverConfig?.datadogRumPrivacyLevel ||
      process.env.REACT_APP_CBRE_BROWSER_DATADOG_RUM_PRIVACY_LEVEL ||
      'mask-user-input',
  };
  return config;
};

// Initialize RUM configuration
const initializeRum = (serverConfig = {}) => {
  if (typeof window !== 'undefined' && !datadogRum.getInitConfiguration()) {
    const config = getRumConfig(serverConfig);
    const { applicationId, clientToken } = config;
    // Check for required configuration
    if (!applicationId || !clientToken) {
      console.warn(
        'Datadog RUM Configuration Warning: Missing required configuration.\n' +
          'Required variables:\n' +
          `- APPLICATION_ID: ${applicationId ? 'Set' : 'Missing'}\n` +
          `- CLIENT_TOKEN: ${clientToken ? 'Set' : 'Missing'}\n` +
          'RUM monitoring will be disabled. Please check your configuration.',
      );
      return false;
    }

    try {
      datadogRum.init({
        applicationId,
        clientToken,
        site: config.site,
        service: config.service,
        env: config.env,
        version: process.env.REACT_APP_VERSION || '1.0.0',
        sessionSampleRate: config.sessionSampleRate,
        sessionReplaySampleRate: config.sessionReplaySampleRate,
        defaultPrivacyLevel: config.defaultPrivacyLevel,
      });

      return true;
    } catch (error) {
      console.error('Failed to initialize Datadog RUM:', error);
      return false;
    }
  }

  return true;
};

// RUM Provider component
const RumProvider = ({ children }) => {
  const { uuid, userDisplayName } = useSelector(({ auth }) => auth, shallowEqual);
  const serverConfig = useSelector((state) => state.config?.client || {});

  const [isRumInitialized, setIsRumInitialized] = React.useState(false);
  // Add a check to determine if we have valid configuration
  const hasValidConfig = React.useMemo(() => {
    if (!serverConfig) return false;
    return !!(serverConfig.datadogRumApplicationId && serverConfig.datadogRumClientToken);
  }, [serverConfig]);

  // Initialize RUM only when we have valid configuration
  useEffect(() => {
    if (typeof window !== 'undefined' && hasValidConfig && !isRumInitialized) {
      const initialized = initializeRum(serverConfig);
      setIsRumInitialized(initialized);
    }
  }, [serverConfig, hasValidConfig, isRumInitialized]);

  // Set user information when uuid changes (only if RUM is initialized)
  useEffect(() => {
    if (typeof window !== 'undefined' && uuid && isRumInitialized && datadogRum.getInitConfiguration()) {
      try {
        datadogRum.setUser({
          id: uuid,
          name: userDisplayName,
          // Add more user properties as needed
        });
      } catch (error) {
        console.warn('Failed to set RUM user:', error);
      }
    }
  }, [uuid, isRumInitialized]);
  return children;
};

// Export RUM utilities for manual tracking
export const trackError = (error, context = {}) => {
  if (typeof window !== 'undefined' && datadogRum && datadogRum.getInitConfiguration()) {
    try {
      datadogRum.addError(error, context);
    } catch (err) {
      console.warn('Failed to track error with RUM:', err);
    }
  }
};

export const trackAction = (name, context = {}) => {
  if (typeof window !== 'undefined' && datadogRum && datadogRum.getInitConfiguration()) {
    try {
      datadogRum.addAction(name, context);
    } catch (err) {
      console.warn('Failed to track action with RUM:', err);
    }
  }
};

export const setUser = (user) => {
  if (typeof window !== 'undefined' && datadogRum && datadogRum.getInitConfiguration()) {
    try {
      datadogRum.setUser(user);
    } catch (err) {
      console.warn('Failed to set user with RUM:', err);
    }
  }
};

export const addAttribute = (key, value) => {
  if (typeof window !== 'undefined' && datadogRum && datadogRum.getInitConfiguration()) {
    try {
      datadogRum.addAttribute(key, value);
    } catch (err) {
      console.warn('Failed to add attribute with RUM:', err);
    }
  }
};

export { RumProvider, initializeRum };
 